import type { CreditSpreadChainRow } from '@fleece/models';
import * as fmt from './format';

/**
 * The chain table's columns, and what each one means — in one list, so the table's header
 * tooltips and the column guide cannot describe two different calculations.
 *
 * In the formulas, S is the underlying's price, K a contract's strike, and K′ the strike of
 * its neighbour: the next strike further out of the money in the same expiration.
 */
export interface ChainColumnHelp {
  readonly key: string;
  /** What the table header says. */
  readonly label: string;
  readonly name: string;
  readonly description: string;
  /** One line per case, in plain text. */
  readonly formula?: ReadonlyArray<string>;
  readonly note?: string;
}

export interface ChainColumn extends ChainColumnHelp {
  readonly render: (row: CreditSpreadChainRow) => string;
}

/** The first column, drawn by the table itself because it carries the pick badge. */
export const STRIKE_HELP: ChainColumnHelp = {
  key: 'strike',
  label: 'Strike',
  name: 'Strike',
  description:
    'The price the option can be exercised at. Only out-of-the-money contracts 7 to 45 days to expiration are listed, nearest the money first. Tap two strikes in the same expiration to build a spread: the one nearer the money is sold (S), the other bought (L).',
};

export const CHAIN_COLUMNS: ReadonlyArray<ChainColumn> = [
  {
    key: 'moneyness',
    label: 'OTM',
    name: 'Moneyness',
    description: 'How far out of the money the strike is, as a share of the underlying price. Both sides divide by S, so 5% means the same distance on a put as on a call.',
    formula: ['Call: (K − S) / S', 'Put: (S − K) / S'],
    note: 'S is the underlying’s last regular-session trade, shown beside the ticker. Option quotes freeze at the 4 pm close, so an after-hours stock price would shift every moneyness while no option moved.',
    render: (row) => fmt.pct(row.moneyness, 1),
  },
  {
    key: 'bid',
    label: 'Bid',
    name: 'Bid',
    description: 'The best price anyone is bidding for the contract — what you receive selling it at market.',
    note: 'From Alpaca’s latest OPRA quote. Outside market hours this is the quote as it stood at the close.',
    render: (row) => fmt.price(row.bid),
  },
  {
    key: 'ask',
    label: 'Ask',
    name: 'Ask',
    description: 'The best price anyone is offering the contract at — what you pay buying it at market.',
    note: 'A quote with no offer (ask of 0) is treated as unquoted, and its bid, ask and mid are left blank.',
    render: (row) => fmt.price(row.ask),
  },
  {
    key: 'spread',
    label: 'Sprd',
    name: 'Bid/ask spread',
    description: 'What a round trip costs as a share of the contract’s fair value: buying at the ask and selling at the bid loses this much of the mid.',
    formula: ['mid = (bid + ask) / 2', 'Sprd = (ask − bid) / mid'],
    note: 'Relative to the mid rather than to (ask + bid), which would read half as large. A zero bid gives 200%: nobody will pay anything for it.',
    render: (row) => fmt.pct(row.spreadPct, 0),
  },
  {
    key: 'last',
    label: 'Last',
    name: 'Last trade',
    description: 'The price of the most recent trade in the contract.',
    note: 'Can be hours or days old for a far strike that rarely trades. Bid and ask say what the market is now; Last says what it was.',
    render: (row) => fmt.price(row.last),
  },
  {
    key: 'delta',
    label: 'Δ',
    name: 'Delta',
    description: 'How much the option price moves per $1 in the underlying — and, roughly, the chance it expires in the money. Negative for puts.',
    note: 'As Alpaca computes it; about 4 contracts in 10 have none. After hours it is solved against the moving stock price and the frozen option quote, so read it during the session.',
    render: (row) => (row.delta === undefined ? fmt.NA : row.delta.toFixed(2)),
  },
  {
    key: 'diff',
    label: 'Nbr Δ$',
    name: 'Neighbour price difference',
    description: 'The credit, at mid, of a one-strike spread that sells this contract and buys its neighbour.',
    formula: ['Nbr Δ$ = mid(K) − mid(K′)', 'Call: K′ = next higher strike', 'Put: K′ = next lower strike'],
    note: 'Blank for the furthest strike, which has no neighbour, and wherever either contract is unquoted. Mids rather than last trades, because last trades of two strikes can be hours apart.',
    render: (row) => fmt.price(row.neighborPriceDiff),
  },
  {
    key: 'rel',
    label: '/ width',
    name: 'Credit per dollar of width',
    description:
      'The neighbour difference divided by the strike gap: the credit a one-strike spread collects per dollar it can lose. Comparable across strikes whose spacing changes, such as 2.5 near the money and 5 further out.',
    formula: ['/ width = Nbr Δ$ / |K − K′|', 'max loss per $ of credit = (1 − x) / x, where x = / width'],
    note: 'At 20% a spread collects $1 for every $5 of width and risks $4 to make it.',
    render: (row) => fmt.pct(row.neighborRelativeDiff, 1),
  },
];
